import { IBaseNavRoute, INavRoute, Nav } from 'app/components'
import { Container } from 'app/containers/Container'
import { markAppActive } from 'app/containers/RetirementApp/IdletimeManager'
import { LoggedInAppNavRoutes, LoggedOutAppNavRoutes } from 'app/containers/RetirementApp/routes'
import { Profile, SectionType } from 'app/redux/Profile/model'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Panel } from 'react-bootstrap'
import { Provider } from 'react-redux'
import { HashRouter, Redirect, Route, Switch } from 'react-router-dom'

// the maximum time we let a user be idle before forcing a logout.
const maxUserIdletimeBeforeLogout = moment.duration(10, 'minutes')

interface IRetirementAppState {
  NavRoutes: INavRoute[]
  isLoggedIn: boolean
  NavHeaderElement: JSX.Element
  Breadcrumb: string
  BreadcrumbPath: string
  currentPath: string
  isInit: boolean
  username: string
  profile: Profile
  showMore: boolean
}

function getDocumentHeight() {
  let r = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight
  )
  const e = document.getElementById('mainPage')
  if (e) {
    r = Math.max(r,
      e.clientHeight,
      e.scrollHeight,
      e.offsetHeight
    )
  }
  return r
}

class RetirementApp extends Container<{}, IRetirementAppState> {

  state: IRetirementAppState = {
    NavRoutes: [],
    isLoggedIn: false,
    NavHeaderElement: null,
    Breadcrumb: null,
    BreadcrumbPath: null,
    currentPath: '',
    isInit: false,
    username: '',
    profile: null,
    showMore: false
  }

  brand = this.getCustomerBrandInfo()

  private lastShowMore = false

  resizer() {
    let showMore = false
    if (this.state.isLoggedIn) {
      const documentHeight = getDocumentHeight()
      const viewportHeight = window.innerHeight
      const scrollTop = document.documentElement.scrollTop
      const unseen = documentHeight - viewportHeight - scrollTop
      // tslint:disable-next-line:no-console
      //console.log('window resizer says doc height: ' + documentHeight
      //  + ', viewport height: ' + viewportHeight
      //  + ', scrollTop: ' + scrollTop
      //  + ', unseen: ' + unseen)
      showMore = unseen > 10
    }
    if (this.lastShowMore !== showMore) {
      this.setState({ showMore })
      // tslint:disable-next-line:no-console
      //console.log('showMore: ' + showMore)
      this.lastShowMore = showMore
    }
  }

  componentWillMount() {

    /**
     * Called to signal that the user is alive and using the app. If
     * the debounce time expires, then the user has gone idle too long
     * and we'll force a logout.
     */
    const tickleDeadman = _.debounce(() => {
      if (this.state.isLoggedIn) {
        // tslint:disable-next-line:no-console
        console.log('The user has been idle for more than ' + maxUserIdletimeBeforeLogout.as('minutes') + ' minute -- forcing a logout...')
        this.logout()
      }
    }, maxUserIdletimeBeforeLogout.as('milliseconds'))

    this.Actions.App.init()
    this.Actions.Nav.showAdequateHeader()
    this.Actions.Nav.setDefaultNavRoutes(LoggedInAppNavRoutes)

    this.onAppStateUpdate((appState) => {

      const userSubstate = appState.User.getSome('username', 'isInit')

      const isLoggedIn = !!userSubstate.username

      if (isLoggedIn) {
        // if the user IS logged in, and we get -any- type of app state update, the user must be alive...
        markAppActive()
        tickleDeadman()
      }

      const isInit = userSubstate.isInit
      const username = userSubstate.username

      const NavHeaderElement = appState.Nav.get('NavHeaderElement')
      const NavRoutes = appState.Nav.get('NavRoutes')
      const Breadcrumb = appState.Nav.get('Breadcrumb')
      const BreadcrumbPath = appState.Nav.get('BreadcrumbPath')

      const profile = appState.Profile.getNative('info')

      this.setState({
        isInit, isLoggedIn, NavHeaderElement, NavRoutes, Breadcrumb, username, BreadcrumbPath, profile
      })

      setTimeout(() => this.resizer(), 300)
    })

    this.onLocationUpdate(
      ({ hash: currentPath }) => this.setState({ currentPath })
    )

    window.addEventListener('scroll', () => this.resizer())
    window.addEventListener('resize', () => this.resizer())
  }

  /**
   * AppNavRoutes to <Route /> Elements
   * @param navRoute
   * @param index
   */
  mapAppNavRoutesToReactRoutes(navRoute: INavRoute) {
    if (navRoute.single) {
      return (
        <Route key={navRoute.single.path} exact={!!navRoute.single.exact} path={navRoute.single.path} component={navRoute.single.component} />
      )
    }

    if (navRoute.multi) {
      return (
        <Route key={navRoute.multi.path} exact={!!navRoute.multi.exact} path={navRoute.multi.path} />
      )
    }
  }

  logout() {
    this.showSnackbar('Logout Sucessful', 'success')
    this.Actions.User.logout()
  }

  resetProfile(what: SectionType) {
    this.state.profile.resetSection(what)
    this.Actions.Profile.store(this.state.profile)
  }

  render() {
    // wait for initialization to complete
    if (!this.state.isInit) {
      return null
    }

    const RootNavRoutes = this.state.isLoggedIn ? LoggedInAppNavRoutes : LoggedOutAppNavRoutes
    const defaultRoute = RootNavRoutes.find((navRoute) => navRoute.default === true)
    const defaultPath = defaultRoute.single ? defaultRoute.single.path : defaultRoute.multi.routes[0].path
    const showMoreClass = this.state.showMore ? 'fadeIn' : 'fadeOut'

    return (
      <Provider store={this.getStore()} key="provider">
        <HashRouter>

          <section className={this.state.isLoggedIn ? 'logged-in' : 'logged-out'} >
            <Nav currentPath={this.state.currentPath}
              config={this.state.NavRoutes}
              brandImgUrl={this.brand.imgUrl}
              brandText={this.brand.brandName}
              breadcrumbName={this.state.Breadcrumb}
              breadcrumbPath={this.state.BreadcrumbPath}
              leftNavDescription={this.state.NavHeaderElement}
              username={this.state.username}
              onLogoutClicked={() => this.logout()}
              onResetProfile={(a: SectionType) => this.resetProfile(a)}
              profile={this.state.profile} />

            <main className="page" id="mainPage" >
              <Switch>
                {RootNavRoutes.map((route) =>
                  this.mapAppNavRoutesToReactRoutes(route)
                )}
                <Redirect to={defaultPath} />
              </Switch>
            </main>

            <section className="container too-small-vw">
              <Panel style={{ marginTop: 52 }} className="warning-bg">
                <h2>
                  <i className="fa fa-fw fa-exclamation-circle" />
                  <span>Uh oh!</span>
                </h2>

                <p className="lead">
                  <b>Your screen size is too small.</b> Please extend your window's width
                  until the website reappears. Or if you're on a mobile device, revisit
                  us using a larger tablet or desktop.
                </p>
              </Panel>
            </section>

          <div className={`showBelowFold animated ${showMoreClass}`}>
            <img src="/assets/img/Down_Pointing_Backhand.png" width={50} />
          </div>
            <footer id="snackbar"></footer>
          </section>

        </HashRouter>
      </Provider>
    )
  }
}

export {
  RetirementApp
}



// WEBPACK FOOTER //
// ./src/app/containers/RetirementApp/index.tsx